/**
 * Analytics module — powered by PostHog.
 *
 * All tracking flows through this file so the rest of the app never
 * imports PostHog directly. If PostHog is not configured (no API key),
 * all calls are safe no-ops that log to console in __DEV__.
 */
import PostHog from 'posthog-react-native';

// PostHog expects string-indexable JSON values, not `unknown`
type JsonPrimitive = string | number | boolean | null;
type JsonType = JsonPrimitive | JsonType[] | { [key: string]: JsonType };
type Properties = Record<string, JsonType>;

// ── Singleton reference ──────────────────────────────────────────────
// Set once by initAnalytics(), called from App.tsx after PostHog mounts.
let _posthog: PostHog | null = null;

/**
 * Call once at app startup to connect analytics to the PostHog instance.
 */
export function initAnalytics(client: PostHog | null): void {
  _posthog = client;
}

// ── Identity ─────────────────────────────────────────────────────────

/**
 * Identify the current user after sign-in. Call on every auth state change.
 */
export function identifyUser(
  userId: string,
  properties?: Properties,
): void {
  if (__DEV__) {
    console.log('[Analytics] identify:', userId, properties);
  }
  _posthog?.identify(userId, properties);
}

/**
 * Reset identity on sign-out so subsequent events are anonymous.
 */
export function resetAnalyticsUser(): void {
  if (__DEV__) {
    console.log('[Analytics] reset user');
  }
  _posthog?.reset();
}

// ── Core tracking ────────────────────────────────────────────────────

/**
 * Track a named event with optional properties.
 */
export function trackEvent(
  name: string,
  properties: Properties = {},
): void {
  if (__DEV__) {
    console.log('[Analytics] Event:', name, properties);
  }
  _posthog?.capture(name, properties);
}

/**
 * Track a screen view. Call from navigation state change handlers.
 */
export function trackScreen(
  screenName: string,
  properties: Properties = {},
): void {
  if (__DEV__) {
    console.log('[Analytics] Screen:', screenName, properties);
  }
  _posthog?.screen(screenName, properties);
}

// ── Share events (preserves existing interface) ──────────────────────

interface ShareEventData {
  type:
    | 'portfolio_card'
    | 'portfolio_card_download'
    | 'portfolio_card_save'
    | 'copy_link'
    | 'share_messages'
    | 'share_whatsapp'
    | 'share_messenger'
    | 'share_snapchat';
  totalValue: number;
  itemCount: number;
}

export function trackShareEvent(data: ShareEventData): void {
  trackEvent('share_card', {
    share_type: data.type,
    total_value: data.totalValue,
    item_count: data.itemCount,
  });
}

// ── Convenience helpers for key events ───────────────────────────────

export function trackItemAdded(properties: {
  category?: string;
  source: 'photo' | 'room_scan' | 'manual';
  brand?: string;
  estimatedValue?: number;
}): void {
  trackEvent('item_added', properties);
}

export function trackItemAppraised(properties: {
  source: 'photo' | 'camera';
  identified: boolean;
  estimatedValue?: number;
}): void {
  trackEvent('item_appraised', properties);
}

export function trackSignupCompleted(properties: {
  method: 'google' | 'apple' | 'email';
}): void {
  trackEvent('signup_completed', properties);
}

export function trackPriceRefreshed(properties: {
  itemCount: number;
  source?: string;
}): void {
  trackEvent('price_refreshed', properties);
}

export function trackPortfolioViewed(properties: {
  totalValue: number;
  itemCount: number;
}): void {
  trackEvent('portfolio_viewed', properties);
}
